/* eslint-disable react-hooks/exhaustive-deps */
import { Search, RotateCw } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Modal,
  Row,
  Table
} from 'react-bootstrap'
import { useLocation, useNavigate } from 'react-router-dom'
import Select from 'react-select'
import Loader from '../../components/atoms/Loader'
import RowEmpty from '../../components/atoms/RowEmpty'
import { BadgeStatusPrList } from '../../components/atoms/StatusLabel'
import TablePagination from '../../components/atoms/Table/TablePagination'
import SelectInput from '../../components/Input/SelectInput'
import TextField from '../../components/Input/TextInput'
import ModalStockAdjustment from '../../components/modal/stockAdjustment/ModalStockAdjustment'
import api from '../../config/AxiosInstance'
import toast, { Toaster } from 'react-hot-toast'
import { convertDateWithTime } from '../../helpers/constants'
import '../../styles/style.css'

const headerTitleStyle = {
  color: 'var(--primary-orange)',
  fontSize: '24px',
  fontWeight: '600'
}

const tableHeaderStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#333333',
  letterSpacing: '0.5px',
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e9ecef'
}

const cardFilterStyle = {
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  borderRadius: '12px',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  backgroundColor: '#fff'
}

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#333333',
  textTransform: 'uppercase',
  marginBottom: '0.5rem',
  letterSpacing: '0.5px'
}

const SelectStyles = {
  control: (base, state) => ({
    ...base,
    height: 45,
    minHeight: 45,
    borderRadius: '10px',
    borderColor: state.isFocused ? 'var(--primary-orange)' : '#e9ecef',
    boxShadow: state.isFocused ? '0 0 0 4px rgba(248, 157, 29, 0.1)' : 'none',
    backgroundColor: '#ffffff',
    border: '1px solid #e9ecef',
    fontSize: '0.9rem'
  }),
  valueContainer: (provided, state) => ({
    ...provided,
    height: '45px',
    padding: '0 12px'
  }),
  input: (provided, state) => ({
    ...provided,
    margin: '0px'
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#495057'
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? 'var(--primary-orange)'
      : state.isFocused
        ? '#fff5e6'
        : null,
    color: state.isSelected ? 'white' : '#333',
    cursor: 'pointer'
  })
}

const FormControlStyle = {
  height: '45px',
  borderRadius: '10px',
  border: '1px solid #e9ecef',
  backgroundColor: '#ffffff',
  fontSize: '0.9rem'
}

const CYCLE_COUNT_STATUSES = [
  { value: 'waiting', label: 'Waiting' },
  { value: 'ongoing', label: 'Ongoing' },
  { value: 'recheck', label: 'Recheck' },
  { value: 'done', label: 'Done' }
]

const customModalSelectStyles = {
  multiValue: (provided, state) => ({
    ...provided,
    backgroundColor: 'white',
    color: 'black',
    border: '1.2px solid #F1F2F7',
    padding: '1px',
    borderRadius: '5px',
    fontSize: '14px',
    flexDirection: 'row-reverse'
  }),
  control: (base) => ({
    ...base,
    height: 45,
    minHeight: 45,
    borderRadius: '10px'
  }),
  valueContainer: (provided, state) => ({
    ...provided,
    padding: '0 12px'
  }),
  input: (provided, state) => ({
    ...provided,
    margin: '0px'
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999
  })
}

function StockOpnameCycleCount() {
  const { state } = useLocation()
  const navigate = useNavigate()

  const refPics = useRef(null)
  const refCheckers = useRef(null)
  const refSrcWarehouses = useRef()
  const refSrcStatus = useRef()

  const [warehouses, setWarehouses] = useState([])
  const [data, setData] = useState({
    values: [],
    metadata: {
      per_page: 10,
      current_page: 1,
      total_row: 0,
      total_page: 0
    }
  })

  const [warehouseId, setWarehouseId] = useState(state?.warehouse_id || '')
  const [q, setQ] = useState(state?.q || '')
  const [debouncedQ, setDebouncedQ] = useState(state?.q || '')
  const [status, setStatus] = useState(state?.status || '')

  const [users, setUsers] = useState({
    pics: [],
    checkers: []
  })

  const [form, setForm] = useState({
    warehouse_id: null,
    start_time: null,
    end_time: null,
    pics: [],
    checkers: []
  })

  const [showModal, setShowModal] = useState({
    stepOne: false,
    stepTwo: false
  })

  const [loading, setLoading] = useState({
    data: false,
    submit: false
  })

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQ(q)
      setData((prev) => ({
        ...prev,
        metadata: { ...prev.metadata, current_page: 1 }
      }))
    }, 1000)
    return () => clearTimeout(handler)
  }, [q])

  const fetchData = async () => {
    if (!warehouseId) {
      setData({
        values: [],
        metadata: { ...data.metadata, total_row: 0, total_page: 0 }
      })
      return
    }
    try {
      setLoading((prevLoading) => ({ ...prevLoading, data: true }))
      const params = {
        q: debouncedQ,
        warehouse_id: warehouseId,
        status: status,
        type: 'cycle_count',
        per_page: data?.metadata?.per_page || 10,
        page: data?.metadata?.current_page || 1
      }
      const res = await api.get('/stock-opname', { params })
      const { data: dataResponse, metadata: metadataResponse } = res.data
      setData({
        values: dataResponse,
        metadata: metadataResponse
      })
    } catch (error) {
      const errMsg =
        error?.response?.data?.errors?.message ||
        error?.message ||
        'An error occurred'
      toast.error(errMsg, {
        position: 'top-right',
        style: { fontSize: '14px', fontWeight: '600' }
      })
      setData({ ...data, values: [] })
    } finally {
      setLoading((prevLoading) => ({ ...prevLoading, data: false }))
    }
  }

  useEffect(() => {
    fetchData()
  }, [
    data.metadata.current_page,
    data.metadata.per_page,
    warehouseId,
    debouncedQ,
    status
  ])

  useEffect(() => {
    fetchWarehouses()
  }, [])

  const handleReset = () => {
    setQ('')
    setDebouncedQ('')
    setWarehouseId('')
    setStatus('')
    refSrcWarehouses.current?.clearValue()
    refSrcStatus.current?.clearValue()
    setData((prev) => ({
      ...prev,
      metadata: { ...prev.metadata, current_page: 1 }
    }))
  }

  const handlePageChange = (data) => {
    setData((prevData) => ({
      ...prevData,
      metadata: {
        ...prevData.metadata,
        current_page: data.selected + 1
      }
    }))
    window.scrollTo(0, 0)
  }

  const handleOnChangeMeta = (e) => {
    setData((prevData) => ({
      ...prevData,
      metadata: {
        ...prevData.metadata,
        current_page: 1,
        per_page: parseInt(e.target.value, 10)
      }
    }))
    window.scrollTo(0, 0)
  }

  const columns = [
    { key: 'code', label: 'Ticket' },
    { key: 'warehouse_id', label: 'Warehouse' },
    {
      key: 'created_at',
      label: 'Created Date',
      className: 'text-end',
      component: (value) => value?.created_at || '-'
    },
    {
      key: 'finish_at',
      label: 'Finished Date',
      className: 'text-end',
      component: (value) =>
        value?.finish_date !== '-' ? value?.finish_date : '-'
    },
    {
      key: 'status',
      label: 'Status',
      className: 'text-end',
      component: (value) => (
        <div key={value?.id} className="d-flex justify-content-end">
          <BadgeStatusPrList text={value?.status || 'done'} />
        </div>
      )
    },
    {
      key: 'download',
      label: 'Berita Acara',
      className: 'text-end',
      component: (value) => (
        <div key={value?.id}>
          <Button
            variant="transparent"
            className={`${
              value?.status === 'finish'
                ? 'btn-primary-blue'
                : 'btn-secondary opacity-25'
            }`}
            disabled={value?.status !== 'finish'}
            size="sm"
          >
            DOWNLOAD
          </Button>
        </div>
      )
    },
    {
      key: 'action',
      label: 'Action',
      className: 'text-end',
      component: (value) => (
        <div key={value?.id}>
          <button
            className="btn btn-light text-secondary border rounded"
            style={{ width: '32px', height: '32px', padding: 0 }}
            onClick={() => {
              navigate(`/stock-opname/cycle-count/detail/${value?.id}`, {
                state: {
                  q,
                  warehouse_id: warehouseId,
                  status,
                  warehouse_name: value?.warehouse_id
                }
              })
            }}
          >
            <Search size={14} />
          </button>
        </div>
      )
    }
  ]

  const propModal = () => {
    const visibleKey = Object.keys(showModal).find(
      (key) => showModal[key] === true
    )

    if (visibleKey === 'stepOne') {
      return {
        title: 'Create Cycle Count',
        buttonSubmitText: 'Next'
      }
    }

    if (visibleKey === 'stepTwo') {
      return {
        title: 'Select Team',
        buttonSubmitText: 'Submit'
      }
    }
    return {
      title: '',
      description: null
    }
  }

  const handleClose = () => {
    const visibleKey = Object.keys(showModal).find(
      (key) => showModal[key] === true
    )

    if (visibleKey === 'stepTwo') {
      setShowModal({
        stepOne: false,
        stepTwo: false
      })
      setTimeout(() => {
        setShowModal({
          stepOne: true,
          stepTwo: false
        })
      }, 300)
      setForm({
        ...form,
        pics: [],
        checkers: []
      })
    }

    if (visibleKey === 'stepOne') {
      setShowModal({
        stepOne: false,
        stepTwo: false
      })
      setForm({
        warehouse_id: null,
        start_time: null,
        end_time: null,
        pics: [],
        checkers: []
      })
    }
  }

  const handleForm = (value, name) => {
    if (
      name === 'start_time' &&
      value &&
      form.end_time &&
      new Date(value).getTime() > new Date(form.end_time).getTime()
    ) {
      toast.error('Start time cannot be later than end time', {
        position: 'top-right',
        style: { fontSize: '14px', fontWeight: '600' }
      })
      return
    }
    setForm((prevForm) => ({
      ...prevForm,
      [name]: value
    }))
  }

  const handleSubmit = () => {
    setShowModal({
      stepOne: false,
      stepTwo: false
    })
    navigate('/stock-opname/cycle-count/create', {
      state: {
        ...form
      }
    })
  }

  const fetchWarehouses = async () => {
    try {
      const res = await api.get(`/master/warehouses/all`, {
        params: {
          is_parent: true,
          forUser: 1
        }
      })
      const { data: responseData } = res
      if (responseData.success) {
        const options = responseData.data.map((w) => ({
          value: w.id,
          label: w.warehouse_name
        }))
        setWarehouses(options)
      }
    } catch (error) {
      const err =
        error?.response?.data?.errors?.message ||
        error?.message ||
        'An error occurred'
      toast.error(err, {
        position: 'top-right',
        style: { fontSize: '14px', fontWeight: '600' }
      })
    }
  }

  const propDisableButtonModal = () => {
    if (showModal.stepOne) {
      return !form.warehouse_id || !form.start_time || !form.end_time
    }
    if (showModal.stepTwo) {
      return !form.pics.length || !form.checkers.length
    }
    return false
  }

  const fetchChecker = async () => {
    try {
      const payload = {
        warehouse_id: form.warehouse_id,
        type: 'checker'
      }
      const res = await api.post('/stock-opname/users', payload)
      const { data: responseData } = res
      if (responseData.success) {
        setUsers((prev) => ({
          ...prev,
          checkers: responseData?.data?.map(({ id, name }) => ({
            value: id,
            label: name
          }))
        }))
      }
    } catch (error) {
      const err =
        error?.response?.data?.errors?.message ||
        error?.message ||
        'An error occurred'
      toast.error(err, {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
    }
  }

  const fetchPic = async () => {
    try {
      const payload = {
        warehouse_id: form.warehouse_id,
        type: 'pic'
      }
      const res = await api.post('/stock-opname/users', payload)
      const { data: responseData } = res
      if (responseData.success) {
        setUsers((prev) => ({
          ...prev,
          pics: responseData?.data?.map(({ id, name }) => ({
            value: id,
            label: name
          }))
        }))
      }
    } catch (error) {
      const err =
        error?.response?.data?.errors?.message ||
        error?.message ||
        'An error occurred'
      toast.error(err, {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
    }
  }

  const handleStepOneForm = () => {
    setShowModal((prevShowModal) => ({
      ...prevShowModal,
      stepOne: false
    }))
    fetchPic()
    fetchChecker()
    setTimeout(() => {
      setShowModal((prevShowModal) => ({
        ...prevShowModal,
        stepTwo: true
      }))
    }, 1000)
  }

  return (
    <Container fluid className="px-4">
      <Toaster />
      <Row className="mb-4 align-items-center">
        <Col>
          <h3 style={headerTitleStyle}>Cycle Count</h3>
        </Col>
        <Col className="text-end d-flex justify-content-end gap-2">
          <Button
            variant="light"
            className="text-secondary fw-bold rounded px-3 border"
            onClick={handleReset}
            style={{ fontSize: '0.85rem' }}
          >
            <RotateCw size={16} className="me-2" />
            Reset Filters
          </Button>
          <Button
            variant="transparent"
            className="btn-primary-orange fw-bold rounded px-4"
            onClick={() => {
              setShowModal((prevShowModal) => ({
                ...prevShowModal,
                stepOne: true
              }))
              setForm({
                ...form,
                end_time: new Date().toISOString()
              })
            }}
            style={{ fontSize: '0.85rem' }}
          >
            Create
          </Button>
        </Col>
      </Row>

      <Card style={cardFilterStyle}>
        <Row className="g-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label style={labelStyle}>Ticket</Form.Label>
              <InputGroup>
                <InputGroup.Text
                  style={{
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    borderRight: 'none',
                    borderTopLeftRadius: '10px',
                    borderBottomLeftRadius: '10px'
                  }}
                >
                  <Search size={14} className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search Ticket"
                  style={{
                    ...FormControlStyle,
                    borderLeft: 'none',
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0
                  }}
                  onChange={(e) => {
                    setQ(e.target.value)
                  }}
                  value={q}
                />
              </InputGroup>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label style={labelStyle}>Warehouse</Form.Label>
              <Select
                isClearable
                ref={refSrcWarehouses}
                options={warehouses}
                styles={SelectStyles}
                placeholder="All Warehouses"
                onChange={(e) => {
                  setWarehouseId(e?.value || '')
                  setData((prev) => ({
                    ...prev,
                    metadata: { ...prev.metadata, current_page: 1 }
                  }))
                }}
                value={
                  warehouseId
                    ? warehouses.find((w) => w.value === warehouseId)
                    : null
                }
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label style={labelStyle}>Status</Form.Label>
              <Select
                isClearable
                ref={refSrcStatus}
                options={CYCLE_COUNT_STATUSES}
                styles={SelectStyles}
                placeholder="All Statuses"
                onChange={(e) => {
                  setStatus(e?.value || '')
                  setData((prev) => ({
                    ...prev,
                    metadata: { ...prev.metadata, current_page: 1 }
                  }))
                }}
                value={
                  status
                    ? CYCLE_COUNT_STATUSES.find((s) => s.value === status)
                    : null
                }
              />
            </Form.Group>
          </Col>
        </Row>
      </Card>

      <Row>
        <Col md={12} className="my-2">
          <Table
            hover
            responsive
            className="shadow-sm mb-5 bg-white"
            style={{
              borderRadius: '12px',
              overflow: 'hidden',
              fontSize: '0.9rem'
            }}
          >
            <thead>
              <tr>
                {columns.map((column) => (
                  <th
                    className={`p-2 align-middle ${column.className || ''}`}
                    key={column.key}
                    style={tableHeaderStyle}
                  >
                    <span>{column.label}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading.data && (
                <tr>
                  <td colSpan={columns.length} className="p-5 text-center">
                    <Loader />
                  </td>
                </tr>
              )}
              {!loading.data && data?.values?.length === 0 && (
                <RowEmpty colSpan={columns.length} />
              )}
              {!loading.data &&
                data?.values?.length > 0 &&
                data?.values?.map((item, index) => (
                  <tr key={`${item.id}-${index}`}>
                    <td className="p-2 align-middle fw-bold">
                      {item.code || '-'}
                    </td>
                    <td className="p-2 align-middle">{item.warehouse_id}</td>
                    <td className="p-2 align-middle text-end">
                      {item.created_at}
                    </td>
                    <td className="p-2 align-middle text-end">
                      {item.finish_date !== '-' ? item.finish_date : '-'}
                    </td>
                    <td className="p-2 align-middle text-end">
                      <BadgeStatusPrList text={item.status || 'done'} />
                    </td>
                    <td className="p-2 align-middle text-end">
                      <Button
                        variant="transparent"
                        className={`${
                          item.status === 'finish'
                            ? 'btn-primary-blue'
                            : 'btn-secondary opacity-25'
                        }`}
                        disabled={item.status !== 'finish'}
                        size="sm"
                      >
                        DOWNLOAD
                      </Button>
                    </td>
                    <td className="p-2 align-middle text-end">
                      <button
                        className="btn btn-light text-secondary border rounded"
                        style={{ width: '32px', height: '32px', padding: 0 }}
                        onClick={() => {
                          navigate(
                            `/stock-opname/cycle-count/detail/${item.id}`,
                            {
                              state: {
                                q,
                                warehouse_id: warehouseId,
                                status,
                                warehouse_name: item.warehouse_id
                              }
                            }
                          )
                        }}
                      >
                        <Search size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={columns.length} className="p-3 border-top-0">
                  <TablePagination
                    meta={data.metadata}
                    handlePageChange={handlePageChange}
                    handleOnChangeMeta={handleOnChangeMeta}
                  />
                </td>
              </tr>
            </tfoot>
          </Table>
        </Col>
      </Row>

      <ModalStockAdjustment
        show={Object.values(showModal).includes(true)}
        onHide={handleClose}
        title={propModal().title}
        size="md"
        backdrop="static"
      >
        <Modal.Body className="my-2">
          {showModal.stepOne && (
            <>
              <div className="mb-4 text-muted opacity-75">
                <p className="mb-1">
                  Make sure there are no other activities beside this opname!
                </p>
                <p>Select warehouse to create annual opname</p>
              </div>
              <SelectInput
                label="warehouse"
                name="warehouse"
                id="warehouse"
                handleChange={(e) => handleForm(e.target.value, 'warehouse_id')}
                options={warehouses || []}
                value={form.warehouse_id || ''}
                required={true}
                style={FormControlStyle}
              />
              <div className="d-flex gap-2 w-100 mt-2">
                <TextField
                  type="datetime-local"
                  label="Start Time"
                  col={6}
                  value={
                    form.start_time
                      ? convertDateWithTime(form.start_time, 'YYYY-MM-DD HH:mm')
                      : null
                  }
                  handleChange={(e) => handleForm(e.target.value, 'start_time')}
                  style={FormControlStyle}
                />
                <TextField
                  type="datetime-local"
                  label="End Time"
                  col={6}
                  disabled
                  value={
                    form.end_time
                      ? convertDateWithTime(form.end_time, 'YYYY-MM-DD HH:mm')
                      : null
                  }
                  style={FormControlStyle}
                />
              </div>
            </>
          )}
          {showModal.stepTwo && (
            <div className="my-2">
              <Col md={12}>
                <Form.Label style={labelStyle}>PIC</Form.Label>
                <Select
                  isClearable
                  id="pic"
                  defaultValue={form.pics || []}
                  styles={customModalSelectStyles}
                  onChange={(e) => {
                    handleForm(e || [], 'pics')
                  }}
                  menuPortalTarget={document.body}
                  options={users?.pics || []}
                  placeholder="Select PIC"
                  isSearchable
                  ref={refPics}
                  isMulti
                />
              </Col>
              <Col md={12} className="mt-3 mb-4">
                <Form.Label style={labelStyle}>checker</Form.Label>
                <Select
                  isClearable
                  id="checker"
                  defaultValue={form.checkers || []}
                  styles={customModalSelectStyles}
                  onChange={(e) => {
                    handleForm(e || [], 'checkers')
                  }}
                  menuPortalTarget={document.body}
                  options={users?.checkers || []}
                  placeholder="Select Checker"
                  isSearchable
                  ref={refCheckers}
                  isMulti
                />
              </Col>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-top-0 pt-0">
          <div className="d-flex justify-content-end w-100 px-3 pb-3">
            <Button
              variant="transparent"
              className={`fw-bold rounded px-4 ${
                propDisableButtonModal()
                  ? 'btn-secondary opacity-25'
                  : 'btn-primary-orange'
              }`}
              onClick={() => {
                if (showModal.stepOne) {
                  handleStepOneForm()
                } else {
                  handleSubmit()
                }
              }}
              disabled={propDisableButtonModal()}
            >
              {propModal().buttonSubmitText}
            </Button>
          </div>
        </Modal.Footer>
      </ModalStockAdjustment>
    </Container>
  )
}

export default StockOpnameCycleCount
